import React from 'react';
import { HashRouter, Link } from 'react-router-dom';
import { Route, Switch } from 'react-router';
import PropTypes from 'prop-types';
import Header from './Components/Header';
import { linkHeader } from './Components/Header/styles';
import InfoPanel from './Components/InfoPanel/Senate';
import Overview from './Routes/Senate/Overview';
import District from './Routes/Senate/District';
import { Base, Right, Clearfix, Left } from './Symbols/layout';
import CrossPromo from './Components/CrossPromo';
import MapWithList from './Components/Map/SenateFull';
import ScrollToTop from './Components/ScrollToTop';
import E404 from './Routes/Senate/E404';
import { sortBy, sortByOrder, sortByAsNumbers } from './Utils/Sorters';
import { getHasElectionFinished, getIsElectionInProgress } from './Utils/Getters';
import withDataLoader from './Utils/withDataLoader';
import styled from 'react-emotion';
import { Dash } from './Components/MainTitle/styles';

const TitleDash = styled.div`
	flex: 1 0 100%;
	${Dash} {
		font-size: 2.25rem;
	}
`

export const mergeCandidates = (finalists) => {
	return finalists.reduce((candidates, item) => {
		if (!candidates[item.area.id]) {
			candidates[item.area.id] = [];
		}
		candidates[item.area.id].push(item);
		return candidates;
	}, {});
}

const hasProposingParty = senator => !!senator.proposingParty

class App extends React.Component {

	ref = React.createRef();

	state = {
		districts: [],
		roundIndex: this.props.roundIndex,
		mandates: [],
		senators: [],
		overview: {
			parties: [],
			proposingParties: [],
			elected: [],
			finalists: [],
			meta: [{
				turnout: 0,
				count: 0
			}, {
				turnout: 0,
				count: 0
			}]
		},
		leaders: [{}, {}],
		hasData: false
	}

	componentWillMount(){
		const hash = window.location.hash
		const hashParams = hash.split('/')
		const lastParam = hashParams[hashParams.length - 1] || hashParams[hashParams.length - 2]

		if (['round1', 'round2'].includes(lastParam)){
			this.setState({ roundIndex: lastParam === 'round1' ? 0 : 1 })
			window.location.replace(hash.replace(`/${lastParam}`, ''))
		}
	}

	componentDidMount() {
		this.load();
	}

	mapCandidates = (map) => (candidate) => map[candidate];

	load = () => {
		this.props.loadDataAsync(['vysledky_full.json', 'senators_senators.json']).then(
			([data, seats]) => {
				this.setState(state => {
					const map = data.body.candidates;

					const parties = data.body.parties.sort(sortByAsNumbers(state.roundIndex === 0 ? 'total' : 'senators'));
					const proposingParties = (data.body.proposingParties || []).sort(sortByAsNumbers(state.roundIndex === 0 ? 'total' : 'senators'));

					const senators = seats.body.senators.map(senator => {
						if (this.props.districts.indexOf(senator.area.id) === -1) {
							return senator;
						}

						return data.body.leaders[1][senator.area.id] || data.body.leaders[0][senator.area.id];
					});
					senators.sort(sortByOrder(
						this.props.partyOrder,
						hasProposingParty(senators[0]) ? 'proposingParty' : 'party'
					));

					const elected = data.body.elected.map(this.mapCandidates(map));
					const finalists = data.body.finalists.map(this.mapCandidates(map));
					const mergedCandidates = mergeCandidates(elected.concat(finalists));

					return {
						...state,
						hasData: true,
						overview: {
							parties,
							proposingParties,
							finalists,
							elected,
							meta: data.body.meta
						},
						districts: data.body.districts.map(district => {
							const candidates = district.candidates.map(this.mapCandidates(map));
							return {
								...district,
								candidates: candidates.sort(sortBy(0, 'percent')),
								head2head: mergedCandidates[district.id] ? mergedCandidates[district.id].sort((a, b) => {
									return b.votes[state.roundIndex] - a.votes[state.roundIndex] || b.votes[0] - a.votes[0]
								}) : []
							};
						}),
						leaders: data.body.leaders,
						senators
					};
				});
			}
		);
	}

	toggleRound = () => {
		this.setState(state => {
			const roundIndex = (state.roundIndex + 1) % 2;
			return {
				...state,
				roundIndex,
				overview: {
					...state.overview,
					parties: state.overview.parties.slice().sort(sortByAsNumbers(roundIndex === 0 ? 'total' : 'senators')),
					proposingParties: state.overview.proposingParties.slice().sort(sortByAsNumbers(roundIndex === 0 ? 'total' : 'senators'))
				},
				districts: state.districts.map(district => {
					return {
						...district,
						head2head: district.head2head.slice().sort((a, b) => {
							return b.votes[roundIndex] - a.votes[roundIndex] || b.votes[0] - a.votes[0]
						})
					}
				})
			};
		});
	}

	render() {
		// Prevent rendering of refresh button and info panel when either elections are over or data is not ready yet
		const canUpdate = this.state.hasData && getIsElectionInProgress(this.state.overview.meta[this.state.roundIndex])

		return <HashRouter>
			<ScrollToTop scrollContainer={this.ref}>
				<Base innerRef={this.ref}>
					<TitleDash>
						<Header load={this.load} canUpdate={canUpdate}>
							<Route
								path="/obvod/"
								render={() => (
									<Link to="/" className={linkHeader}>Zpět na celkový přehled</Link>
								)}
							/>
						</Header>
					</TitleDash>
					{canUpdate && <InfoPanel districts={this.state.districts} roundIndex={this.state.roundIndex} />}
					<Left>
						<Switch>
							<Route
								exact
								path="/"
								render={() => {
									return <Overview
										year={this.props.year}
										roundIndex={this.state.roundIndex}
										mandates={this.state.mandates}
										senators={this.state.senators}
										actualMandates={this.props.actualMandates}
										districts={this.state.districts}
										parties={this.state.overview.parties}
										proposingParties={this.state.overview.proposingParties}
										finalists={this.state.overview.finalists}
										elected={this.state.overview.elected}
										meta={this.state.overview.meta[this.state.roundIndex]}
										faces={this.props.faces}
									/>
								}}
							/>
							<Route
								path="/obvod/:id"
								render={({ match }) => {
									const id = parseInt(match.params.id, 10);
									const district = this.state.districts.find(d => d.id === id);

									if (!district) {
										return <E404 year={this.props.year} />;
									}

									return <District
										districtId={id}
										name={district.name}
										candidates={district.candidates}
										head2head={district.head2head}
										meta={district.meta}
										roundIndex={this.state.roundIndex}
										districts={this.props.districts}
										faces={this.props.faces}
										year={this.props.year}
									/>
								}}
							/>
							<Route render={() => <E404 year={this.props.year} />} />
						</Switch>
					</Left>
					<Route
						path={['/obvod/:id', '/']}
						render={({ match }) => {
							const areaId = match.params && match.params.id

							return (
								<Right>
									{getHasElectionFinished(this.state.overview.meta[0]) && <CrossPromo
										toggleRound={this.toggleRound}
										roundIndex={this.state.hasData ? this.state.roundIndex : undefined}
										links={this.props.links}
										isDetail={areaId !== undefined}
									/>}
									<MapWithList
										districts={this.state.districts}
										leaders={this.state.leaders}
										roundIndex={this.state.roundIndex}
										selectedAreaId={areaId}
										meta={this.state.overview.meta}
									/>
								</Right>
							)
						}}
					/>

					<Clearfix />
				</Base>
			</ScrollToTop>
		</HashRouter>;
	}

}

App.propTypes = {
	rootUrl: PropTypes.string.isRequired,
	districts: PropTypes.arrayOf(PropTypes.number).isRequired,
	roundIndex: PropTypes.oneOf([0, 1]),
	year: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]).isRequired,
	actualMandates: PropTypes.array.isRequired,
	faces: PropTypes.object.isRequired,
	links: PropTypes.arrayOf(PropTypes.shape({
		url: PropTypes.string,
		text: PropTypes.string
	}))
};

App.defaultProps = {
	roundIndex: 0,
	actualMandates: [],
	faces: {},
	links: []
}

export default withDataLoader(App)
